import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import axios from "axios";
import { useToast } from "../context/ToastContext";

export type WorkOrderStatus =
  | "draft"
  | "in-production"
  | "qc-hold"
  | "completed"
  | "cancelled"
  | null;

interface StepConfig {
  key: WorkOrderStatus;
  labelKey: string;
}

const STEPS: StepConfig[] = [
  { key: "draft", labelKey: "workOrder.status.draft" },
  { key: "in-production", labelKey: "workOrder.status.inProduction" },
  { key: "qc-hold", labelKey: "workOrder.status.qcHold" },
  { key: "completed", labelKey: "workOrder.status.completed" },
];

const STATUS_ORDER: Record<string, number> = {
  draft: 0,
  "in-production": 1,
  "qc-hold": 2,
  completed: 3,
};

export interface CreatedDocInfo {
  code: string;
  number: string;
}

interface WorkOrderStepperProps {
  workOrderStatus: WorkOrderStatus;
  documentId: string;
  createdDocInfo: CreatedDocInfo[] | null;
  onStatusUpdated: (newStatus: WorkOrderStatus) => void;
}

const WorkOrderStepper: React.FC<WorkOrderStepperProps> = ({
  workOrderStatus,
  documentId,
  createdDocInfo,
  onStatusUpdated,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isUpdating, setIsUpdating] = React.useState(false);

  if (workOrderStatus === null) return null;

  const handleTransition = async (newStatus: WorkOrderStatus) => {
    if (!documentId || isUpdating) return;
    try {
      setIsUpdating(true);
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      await axios.put(
        `${apiUrl}/api/documents-material/${documentId}`,
        { workOrderStatus: newStatus },
        { headers: { "x-auth-token": token ?? "" } }
      );
      onStatusUpdated(newStatus);
    } catch (err: any) {
      showToast(
        err.response?.data?.message ||
          t("errors.saveFailed") ||
          "Status update failed",
        "error"
      );
    } finally {
      setIsUpdating(false);
    }
  };

  if (workOrderStatus === "cancelled") {
    return (
      <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500">
          <X className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
        </span>
        <span className="text-sm font-semibold text-red-700">
          {t("workOrder.status.cancelled")}
        </span>
      </div>
    );
  }

  const currentOrder = STATUS_ORDER[workOrderStatus] ?? 0;

  return (
    <div className="mb-4 space-y-3">
      {/* Stepper */}
      <div className="flex items-center bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
        {STEPS.map((step, index) => {
          const stepOrder = STATUS_ORDER[step.key as string] ?? 0;
          const isDone = stepOrder < currentOrder;
          const isActive = step.key === workOrderStatus;
          const isFuture = stepOrder > currentOrder;

          return (
            <React.Fragment key={step.key}>
              {/* Step item */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Circle */}
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors ${
                    isDone
                      ? "bg-emerald-500 border-emerald-500"
                      : isActive
                      ? "bg-blue-600 border-blue-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {isDone ? (
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                  ) : (
                    <span
                      className={`w-2 h-2 rounded-full ${
                        isActive ? "bg-white" : "bg-gray-300"
                      }`}
                    />
                  )}
                </span>
                {/* Label */}
                <span
                  className={`text-xs font-medium whitespace-nowrap ${
                    isDone
                      ? "text-emerald-600"
                      : isActive
                      ? "text-blue-700"
                      : isFuture
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  {t(step.labelKey)}
                </span>
              </div>
              {/* Connector */}
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 rounded ${
                    stepOrder < currentOrder ? "bg-emerald-400" : "bg-gray-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Transition buttons */}
      <div className="flex items-center gap-2">
        {workOrderStatus === "in-production" && (
          <button
            type="button"
            disabled={isUpdating}
            onClick={() => handleTransition("qc-hold")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-50 text-amber-700 border border-amber-300 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            ) : null}
            {t("workOrder.sendToQc")}
          </button>
        )}
        {workOrderStatus === "qc-hold" && (
          <button
            type="button"
            disabled={isUpdating}
            onClick={() => handleTransition("in-production")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <span className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : null}
            {t("workOrder.releaseFromQc")}
          </button>
        )}
      </div>

      {/* Persistent success banner for every created doc (a single generation call can
          now produce more than one document, decided by the generation matrix) */}
      {createdDocInfo && createdDocInfo.length > 0 && (
        <div className="space-y-2">
          {createdDocInfo.map((info, index) => (
            <div
              key={`${info.code}-${info.number}-${index}`}
              className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </span>
              <span className="text-sm text-emerald-800 font-medium">
                {t("workOrder.docCreatedWithCode", { code: info.code })}{" "}
                &mdash; <span className="font-bold">{info.number}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkOrderStepper;
